'use client';

import { useState } from 'react';
import { styled } from '@linaria/react';
import { NewsCategory } from '@fe-news/constants/categories';
import MemberSubHeader from '@fe-news/components/molecules/MemberHeader/MemberSubHeader';
import EditModal from '@fe-news/components/molecules/EditModal';
import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';
import Link from 'next/link';
import Image from 'next/image';
import { useMyPopTopic } from '@fe-news/context/my-pop-topic-provider';
import { CustomEventName, GTMEventKey, sendGTM } from '@fe-cnyes/fe-common-gtm';

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;

  @media screen and (max-width: ${SCREEN_SIZE.TABLET}px) {
    flex-direction: column;
    row-gap: 16px;
  }
`;

const CompileContainer = styled.div`
  display: flex;
  column-gap: 19px;

  @media screen and (max-width: ${SCREEN_SIZE.TABLET}px) {
    margin-left: auto;
  }
`;

const CompileButton = styled.button<{ imageUrl: string; hoverUrl: string }>`
  display: flex;
  align-items: center;
  column-gap: 4px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.29;
  color: #54b5c6;
  cursor: pointer;

  &:before {
    content: '';
    display: inline-block;
    width: 18px;
    height: 18px;
    background-image: ${({ imageUrl }) => `url(${imageUrl})`};
  }

  &:hover {
    color: #0097b1;

    &:before {
      background-image: ${({ hoverUrl }) => `url(${hoverUrl})`};
    }
  }
`;

const ReadMore = styled(Link)`
  display: flex;
  font-size: 13px;
  letter-spacing: 0.5px;
  color: #e03f19;
  align-items: center;
  line-height: 1;

  img {
    margin-left: 4px;
  }
`;

const onClickHotPopTopic = () => {
  const gtmEvent = {
    [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_BUTTON,
    [GTMEventKey.SECTION]: 'News_Category_我的追蹤',
    [GTMEventKey.CLICK_ITEM]: `追蹤的時事＿熱門時事`
  };
  sendGTM(gtmEvent);
};

const MyPopTopicHeader = () => {
  const { isLoggedIn, onClickLoginModalOpen, refetchMyCollection, myCollectionDataItemsIdArray, myPopTopicData } =
    useMyPopTopic();
  const [isEditModalClosed, setIsEditModalClosed] = useState(true);

  const trackListTitle = new Map<string, string>();

  myCollectionDataItemsIdArray?.forEach(id => {
    trackListTitle.set(id, myPopTopicData.get(id)?.data?.popularTopic.title ?? '');
  });

  const onClickEditModalOpen = () => {
    const gtmEvent = {
      [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_BUTTON,
      [GTMEventKey.SECTION]: 'News_Category_我的追蹤',
      [GTMEventKey.CLICK_ITEM]: `追蹤的時事＿編輯時事`
    };
    sendGTM(gtmEvent);
    setIsEditModalClosed(false);
  };

  const onClickEditModalClose = () => {
    setIsEditModalClosed(true);
  };

  const onClickCompileButton = () => {
    if (!isLoggedIn) {
      onClickLoginModalOpen();
      return;
    }

    onClickEditModalOpen();
  };

  const onClickRefetch = () => {
    refetchMyCollection();
  };

  return (
    <>
      <EditModal
        title='追蹤的時事'
        isEditModalClosed={isEditModalClosed}
        onClickEditModalClose={onClickEditModalClose}
        isPopTopic
        list={myCollectionDataItemsIdArray}
        onClickRefetch={onClickRefetch}
        trackListTitle={trackListTitle}
      />
      <HeaderContainer>
        <MemberSubHeader category={NewsCategory.MY_POP_TOPIC} />
        {myCollectionDataItemsIdArray?.length > 0 && (
          <CompileContainer>
            <CompileButton
              imageUrl='/assets/icons/icon-edit-light-blue.svg'
              hoverUrl='/assets/icons/icon-edit-light-blue.svg'
              onClick={onClickCompileButton}
            >
              編輯時事
            </CompileButton>
            <ReadMore href='/poptopic' onClick={onClickHotPopTopic}>
              熱門時事
              <Image src='/assets/icons/arrows/icon-arrow-right-red.png' alt='Next' width={7} height={10} />
            </ReadMore>
          </CompileContainer>
        )}
      </HeaderContainer>
    </>
  );
};

export default MyPopTopicHeader;
